import { useState, useRef, useEffect } from 'react'
import { Search, Plus, AtSign, List, Layout, Calendar, ChevronDown } from 'lucide-react'
import { Avatar } from './ui/Avatar'
import { ViewSelector } from './ViewSelector'
import { UserMenu } from './UserMenu'
import { HamburgerButton } from './HamburgerButton'
import { MobileMenu } from './MobileMenu'
import { useUser } from '../contexts/UserContext'
import { useLocation, useNavigate } from 'react-router-dom'

interface TopNavigationProps {
    onChatAction?: (action: 'new' | 'history' | 'settings') => void
    onCreateAgent?: () => void
}

export function TopNavigation({ onChatAction, onCreateAgent }: TopNavigationProps = {}) {
    const { user } = useUser()
    const location = useLocation()
    const navigate = useNavigate()
    const [activeTab, setActiveTab] = useState(() => location.pathname.startsWith('/agentes') ? 'agentes' : '')
    const [isViewSelectorOpen, setIsViewSelectorOpen] = useState(false)
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
    const [isMobileView, setIsMobileView] = useState(false)
    const [isAgentsDropdownOpen, setIsAgentsDropdownOpen] = useState(false)

    const viewSelectorRef = useRef<HTMLDivElement>(null)
    const userMenuRef = useRef<HTMLDivElement>(null)
    const agentsDropdownRef = useRef<HTMLDivElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)

    // Update active tab based on current path
    useEffect(() => {
        if (location.pathname.startsWith('/agentes')) {
            // eslint-disable-next-line
            if (activeTab !== 'agentes') setActiveTab('agentes')
        } else {
            if (activeTab === 'agentes') setActiveTab('')
        }
    }, [location.pathname, activeTab])

    // ResizeObserver to detect container width changes
    useEffect(() => {
        if (!containerRef.current) return

        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                if (entry.contentRect.width > 0) {
                    requestAnimationFrame(() => {
                        setIsMobileView(entry.contentRect.width < 768)
                    })
                }
            }
        })

        observer.observe(containerRef.current)

        return () => observer.disconnect()
    }, [])

    // Close mobile menu when switching to desktop view
    useEffect(() => {
        if (!isMobileView && isMobileMenuOpen) {
            const timer = setTimeout(() => setIsMobileMenuOpen(false), 0)
            return () => clearTimeout(timer)
        }
    }, [isMobileView, isMobileMenuOpen])

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (viewSelectorRef.current && !viewSelectorRef.current.contains(event.target as Node)) {
                setIsViewSelectorOpen(false)
            }
            if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
                setIsUserMenuOpen(false)
            }
            if (agentsDropdownRef.current && !agentsDropdownRef.current.contains(event.target as Node)) {
                setIsAgentsDropdownOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    // Swipe-right gesture to open mobile menu
    useEffect(() => {
        if (!isMobileView) return

        let touchStartX = 0
        let touchStartY = 0

        const handleTouchStart = (e: TouchEvent) => {
            touchStartX = e.touches[0].clientX
            touchStartY = e.touches[0].clientY
        }

        const handleTouchMove = (e: TouchEvent) => {
            // Only trigger from left edge (first 50px)
            if (touchStartX > 50) return

            const touchCurrentX = e.touches[0].clientX
            const touchCurrentY = e.touches[0].clientY
            const diffX = touchCurrentX - touchStartX
            const diffY = Math.abs(touchCurrentY - touchStartY)

            // Horizontal swipe from left edge (>100px right, <50px vertical)
            if (diffX > 100 && diffY < 50 && !isMobileMenuOpen) {
                setIsMobileMenuOpen(true)
            }
        }

        document.addEventListener('touchstart', handleTouchStart, { passive: true })
        document.addEventListener('touchmove', handleTouchMove, { passive: true })

        return () => {
            document.removeEventListener('touchstart', handleTouchStart)
            document.removeEventListener('touchmove', handleTouchMove)
        }
    }, [isMobileView, isMobileMenuOpen])

    return (
        <>
            <div ref={containerRef} className="h-12 flex items-center justify-between px-4 relative z-50">
                {/* Mobile: Hamburger (Left) + Logo (Center) + Avatar (Right) */}
                {isMobileView && (
                    <>
                        {/* Hamburger Button - Left */}
                        <HamburgerButton
                            isOpen={isMobileMenuOpen}
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        />

                        {/* Logo - Centered (Absolute) */}
                        <div className="absolute left-1/2 -translate-x-1/2">
                            <img
                                src="/assets/hia-logo.png"
                                alt="HIA"
                                className="h-7 w-auto"
                                style={{ mixBlendMode: 'screen' }}
                            />
                        </div>
                    </>
                )}

                {/* Desktop: Left Tabs */}
                {!isMobileView && (
                    <div className="flex items-center h-full gap-4">
                        {/* Logo HIA */}
                        <img
                            src="/assets/hia-logo.png"
                            alt="HIA"
                            className="h-7 w-auto"
                            style={{ mixBlendMode: 'screen' }}
                        />

                        <div className="relative h-full flex items-center" ref={agentsDropdownRef}>
                            <button
                                onClick={() => setIsAgentsDropdownOpen(!isAgentsDropdownOpen)}
                                className={`
                                    flex items-center gap-2 px-3 h-full border-b-2 transition-colors text-sm font-medium
                                    ${activeTab === 'agentes'
                                        ? 'border-gray-900 dark:border-white text-gray-900 dark:text-white'
                                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600'
                                    }
                                `}
                            >
                                <div className={`p-0.5 rounded ${activeTab === 'agentes' ? 'bg-purple-100' : ''}`}>
                                    <AtSign className={`w-4 h-4 ${activeTab === 'agentes' ? 'text-purple-600' : 'text-gray-500 dark:text-gray-400'}`} />
                                </div>
                                <span className="hidden xl:block">Agentes</span>
                                <ChevronDown className={`w-3 h-3 ml-0.5 transition-transform ${isAgentsDropdownOpen ? 'rotate-180' : ''}`} />
                            </button>

                            {/* Dropdown Menu */}
                            {isAgentsDropdownOpen && (
                                <div className="absolute top-full left-0 mt-1 w-48 bg-white dark:bg-gray-900 rounded-xl shadow-lg border border-gray-200 dark:border-gray-800 py-1 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                                    <button
                                        onClick={() => {
                                            navigate('/agentes')
                                            setIsAgentsDropdownOpen(false)
                                        }}
                                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-left"
                                    >
                                        <List className="w-4 h-4" />
                                        Lista de Agentes
                                    </button>
                                    <button
                                        onClick={() => {
                                            if (onCreateAgent) {
                                                onCreateAgent()
                                            } else {
                                                navigate('/agentes/criar')
                                            }
                                            setIsAgentsDropdownOpen(false)
                                        }}
                                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-left"
                                    >
                                        <Plus className="w-4 h-4" />
                                        Criar Agente
                                    </button>
                                </div>
                            )}
                        </div>

                        <NavTab
                            isActive={activeTab === 'lista'}
                            onClick={() => setActiveTab('lista')}
                            icon={List}
                            label="Lista"
                        />
                        <NavTab
                            isActive={activeTab === 'quadro'}
                            onClick={() => setActiveTab('quadro')}
                            icon={Layout}
                            label="Quadro"
                            iconColor="text-blue-600"
                        />
                        <NavTab
                            isActive={activeTab === 'calendario'}
                            onClick={() => setActiveTab('calendario')}
                            icon={Calendar}
                            label="Calendário"
                            iconColor="text-orange-500"
                        />

                        <div className="relative" ref={viewSelectorRef}>
                            <button
                                onClick={() => setIsViewSelectorOpen(!isViewSelectorOpen)}
                                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors ml-1 ${isViewSelectorOpen ? 'bg-gray-100 dark:bg-gray-700' : ''}`}
                            >
                                <Plus className="w-4 h-4" />
                                Visualização
                            </button>
                            <ViewSelector
                                isOpen={isViewSelectorOpen}
                                onClose={() => setIsViewSelectorOpen(false)}
                            />
                        </div>
                    </div>
                )}

                {/* Right Actions */}
                <div className="flex items-center gap-4">
                    {!isMobileView && (
                        <div className="relative group w-full max-w-64 min-w-[200px]">
                            {/* Gradient border wrapper */}
                            <div className="rounded-full p-[1px] bg-gray-200 dark:bg-gray-800 group-focus-within:dark:bg-gradient-to-br group-focus-within:dark:from-blue-400/40 group-focus-within:dark:via-purple-500/40 group-focus-within:dark:to-pink-400/30 transition-all duration-200">
                                <div className="relative bg-white dark:bg-gray-950 rounded-full">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 dark:text-gray-500 z-10" />
                                    <input
                                        type="text"
                                        placeholder="Pesquisar CtrlK"
                                        className="w-full pl-9 pr-8 h-7 bg-transparent border-0 rounded-full dark:text-white dark:placeholder-gray-500 focus:ring-0 text-sm focus:outline-none"
                                    />
                                    <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
                                        <div className="w-1.5 h-1.5 rounded-full bg-pink-500" />
                                        <div className="w-1.5 h-1.5 rounded-full bg-purple-500" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {user ? (
                        <div className="relative" ref={userMenuRef}>
                            <button
                                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                                className="focus:outline-none"
                            >
                                <Avatar size="sm" alt={user.name} src={user.avatar} />
                                <div className={`absolute bottom-0 right-0 w-2.5 h-2.5 border-2 border-white dark:border-black rounded-full ${user.status === 'online' ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                            </button>
                            <UserMenu
                                isOpen={isUserMenuOpen}
                                onClose={() => setIsUserMenuOpen(false)}
                            />
                        </div>
                    ) : (
                        <button
                            onClick={() => window.location.href = '/login'}
                            className="text-sm font-medium text-gray-700 dark:text-gray-200 hover:text-black dark:hover:text-white transition-colors"
                        >
                            Entrar
                        </button>
                    )}
                </div>
            </div>

            {/* Mobile Menu */}
            <MobileMenu
                isOpen={isMobileMenuOpen}
                onClose={() => setIsMobileMenuOpen(false)}
                onChatAction={onChatAction}
            />
        </>
    )
}

interface NavTabProps {
    isActive: boolean
    onClick: () => void
    icon: React.ComponentType<{ className?: string }>
    label: string
    color?: string
    bgColor?: string
    iconColor?: string
}

function NavTab({ isActive, onClick, icon: Icon, label, color, bgColor, iconColor }: NavTabProps) {
    return (
        <button
            onClick={onClick}
            className={`
                flex items-center gap-2 px-3 h-full border-b-2 transition-colors text-sm font-medium
                ${isActive
                    ? 'border-gray-900 dark:border-white text-gray-900 dark:text-white'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600'
                }
            `}
        >
            <div className={`
                p-0.5 rounded
                ${isActive && bgColor ? bgColor : ''}
            `}>
                <Icon className={`
                    w-4 h-4 
                    ${isActive && color ? color : (iconColor || 'text-gray-500 dark:text-gray-400')}
                `} />
            </div>
            <span className={`${isActive ? 'block' : 'hidden xl:block'}`}>{label}</span>
        </button>
    )
}
